import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// Straight from the API layer, not through AuthContext: an availability lookup
// is not session state, and nothing about it survives this dialog closing.
import { checkUsername } from '@/api/auth';
import { useAuth } from '@/context/AuthContext';
import styles from './LoginDialog.module.css';

/**
 * Asks for the playTUB credentials that a purchase needs — or makes them.
 *
 * Used from three places: the header and the purchases page, where signing in
 * is offered on its own, and checkout, where it stands between "Pay" and the
 * order actually being placed — hence `submitLabel`.
 *
 * Two tabs, the same pair playTUB's own login screen offers. Registering here
 * does NOT create a marketplace-only account: it creates a playTUB one, on the
 * external auth service both apps authenticate against, so the books bought in
 * the next minute unlock on the login the reader just chose. See api/auth.js.
 *
 * Credentials are handed straight to that service through AuthContext; nothing
 * is kept here beyond the current keystroke.
 */

/**
 * The provider's own password rule, applied before the round trip.
 *
 * Mirrors `check_password_strength` in tubstudio's auth service (and playTUB's
 * copy of it in its login screen): weak is under 6 characters, or 6+ using only
 * one kind of character. Anything weak is refused there, so checking here only
 * changes how quickly the reader hears about it — the server is still the judge.
 */
function passwordStrength(password) {
  const kinds = [/[A-Z]/, /[a-z]/, /\d/, /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;/~`]/].filter((re) =>
    re.test(password),
  ).length;

  if (password.length >= 10 && kinds >= 4) return 'strong';
  if (password.length >= 8 && kinds >= 3) return 'moderate';
  if (password.length >= 6 && kinds >= 2) return 'fair';
  return 'weak';
}

const STRENGTH_STEPS = ['weak', 'fair', 'moderate', 'strong'];

const WEAK_PASSWORD =
  'Please use at least 6 characters, mixing two of: capitals, lowercase, digits, symbols.';

export function LoginDialog({
  open,
  onClose,
  onSuccess,
  reason,
  submitLabel = 'Sign in',
  // What the same button says once the reader is creating an account rather
  // than signing in. Checkout overrides it so the button still names what
  // happens next.
  registerLabel = 'Create account',
}) {
  const { signIn, signUp } = useAuth();
  const usernameRef = useRef(null);

  const [mode, setMode] = useState('signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // Everything the provider stores as profile detail. Optional to a fault: an
  // account with none of it still works, and a reader buying a book should not
  // be interviewed first.
  const [details, setDetails] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    country: '',
    address: '',
    dob: '',
  });
  const [showMore, setShowMore] = useState(false);
  // null | 'checking' | 'taken' | 'free' — advisory, see checkUsername.
  const [nameStatus, setNameStatus] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const isRegister = mode === 'register';
  const strength = passwordStrength(password);

  // A date of birth must be at least a year ago — the same bound playTUB's
  // registration puts on the field, enforced by the picker rather than by an
  // error after the fact.
  const maxDob = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  }, []);

  // Escape closes, and the field is focused on open so the keyboard lands in
  // the right place — same contract as the cart drawer.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    usernameRef.current?.focus();

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Every opening starts clean. A stale error under a retyped password reads as
  // though the new attempt already failed — and leaving `busy` set from a
  // previous success left the dialog permanently showing "Signing in…" with a
  // disabled button, so it could only ever be used once per visit.
  useEffect(() => {
    if (!open) return;
    setMode('signin');
    setUsername('');
    setPassword('');
    setDetails({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      country: '',
      address: '',
      dob: '',
    });
    setShowMore(false);
    setNameStatus(null);
    setError('');
    setNotice('');
    setBusy(false);
  }, [open]);

  if (!open) return null;

  const setDetail = (key) => (e) => setDetails((d) => ({ ...d, [key]: e.target.value }));

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setNotice('');
    setNameStatus(null);
    // The username carries over — it is the same answer to the same question,
    // and retyping it is the first thing a wrong guess at the tab costs you.
  };

  /**
   * Warn about a taken username while the form is still being filled in.
   *
   * On blur rather than on every keystroke: a half-typed name is not a question
   * worth asking. Silent on failure — an unanswered lookup must not look like a
   * verdict, and creating the account is what actually decides.
   */
  const handleUsernameBlur = async () => {
    const name = username.trim();
    if (!isRegister || !name) {
      setNameStatus(null);
      return;
    }

    setNameStatus('checking');
    try {
      const { exists } = await checkUsername(name);
      // Read back from the field, not from state: this closure was built around
      // the name as it was, and an answer about a name since retyped must not
      // be shown against the new one.
      if (usernameRef.current?.value.trim() !== name) return;
      setNameStatus(exists ? 'taken' : 'free');
    } catch {
      setNameStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;

    setError('');
    setNotice('');

    if (isRegister) {
      if (!username.trim() || !password) {
        setError('Please choose a username and a password.');
        return;
      }
      if (nameStatus === 'taken') {
        setError('That username is taken. Please choose another, or sign in instead.');
        return;
      }
      if (strength === 'weak') {
        setError(WEAK_PASSWORD);
        return;
      }
      // Required, and refused here as well as by the server — which refuses it
      // too, because this form is not the only way to reach /api/auth/register.
      if (!details.dob) {
        setError('Please enter your date of birth.');
        return;
      }
      if (details.dob > maxDob) {
        setError('Date of birth must be at least one year before today.');
        return;
      }
    }

    setBusy(true);
    try {
      const user = isRegister
        ? await signUp({ username, password, ...details })
        : await signIn({ username, password });
      // Cleared before handing over: the caller may keep this dialog mounted,
      // and nothing should be left holding the credentials or the busy flag.
      setPassword('');
      setBusy(false);
      onSuccess(user);
    } catch (err) {
      // The account was created and only the session failed — sending them back
      // to registration would fail on a name that is now their own.
      if (err.accountCreated) {
        setMode('signin');
        setNotice('Your account is ready. Please sign in.');
        setPassword('');
      } else {
        setError(
          err.message ??
            (isRegister
              ? "That didn't work. Please try again."
              : "That username or password isn't right."),
        );
      }
      setBusy(false);
    }
  };

  // Rendered into <body>, not where it was mounted.
  //
  // The header sets `backdrop-filter`, and a filtered element becomes the
  // containing block for its fixed-position descendants — so `top: 50%` was
  // half the HEADER's height, not half the viewport's, and `translate(-50%, -50%)`
  // then lifted the dialog's top edge off the top of the screen, taking the
  // title and the tabs with it. `max-height` could not save it: vh units are
  // viewport-relative, so they never clamped it to the strip it was trapped in.
  // The scrim was confined to that strip too, leaving most of the page
  // un-clickable for dismissal.
  //
  // A portal puts it beyond any such ancestor, whichever of the three callers
  // opened it.
  return createPortal(
    <>
      <button
        type="button"
        className={styles.scrim}
        aria-label="Cancel sign in"
        tabIndex={-1}
        onClick={onClose}
      />

      {/* Its own <form>, separate from the checkout form it sits over — Enter
          here must sign in rather than place the order. The portal keeps the two
          apart in the DOM as well, so there is no nested form to be invalid. */}
      <form
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-title"
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 id="login-title" className={styles.title}>
          {isRegister ? 'Join playTUB' : 'Sign in to playTUB'}
        </h2>

        {/* Tabs, not plain buttons: the choice between them is what the rest of
            the dialog is, and role="tab" also keeps "Sign in" the name of one
            control here — the submit button — rather than two. */}
        <div className={styles.tabs} role="tablist" aria-label="Sign in or create an account">
          <button
            type="button"
            role="tab"
            id="login-tab-signin"
            aria-selected={mode === 'signin'}
            aria-controls="login-panel"
            className={mode === 'signin' ? styles.tabOn : styles.tab}
            onClick={() => switchMode('signin')}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            id="login-tab-register"
            aria-selected={isRegister}
            aria-controls="login-panel"
            className={isRegister ? styles.tabOn : styles.tab}
            onClick={() => switchMode('register')}
          >
            Create account
          </button>
        </div>

        <div
          id="login-panel"
          role="tabpanel"
          aria-labelledby={isRegister ? 'login-tab-register' : 'login-tab-signin'}
        >
          <p className={styles.blurb}>
            {isRegister
              ? 'One account for both: what you buy here opens in playTUB with the login you choose now.'
              : (reason ??
                'Your books are unlocked on your playTUB account, so we need to know who you are.')}
          </p>

          <label className={styles.label} htmlFor="login-username">
            Username
          </label>
          <input
            id="login-username"
            ref={usernameRef}
            className={styles.input}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setNameStatus(null);
            }}
            onBlur={handleUsernameBlur}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck="false"
          />

          {isRegister && nameStatus === 'taken' && (
            <p className={styles.hintBad}>That username is taken.</p>
          )}
          {isRegister && nameStatus === 'free' && (
            <p className={styles.hintGood}>That username is free.</p>
          )}

          <label className={styles.label} htmlFor="login-password">
            Password
          </label>
          <input
            id="login-password"
            type="password"
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isRegister ? 'new-password' : 'current-password'}
          />

          {isRegister && password && (
            <div className={styles.strength}>
              <div className={styles.strengthBar}>
                {STRENGTH_STEPS.map((step, i) => (
                  <span
                    key={step}
                    className={
                      i <= STRENGTH_STEPS.indexOf(strength) ? styles[strength] : styles.off
                    }
                  />
                ))}
              </div>
              <span className={styles.strengthWord}>{strength}</span>
            </div>
          )}

          {isRegister && (
            <>
              <div className={styles.row}>
                <div>
                  <label className={styles.label} htmlFor="signup-firstname">
                    First name
                  </label>
                  <input
                    id="signup-firstname"
                    className={styles.input}
                    value={details.firstname}
                    onChange={setDetail('firstname')}
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className={styles.label} htmlFor="signup-lastname">
                    Last name
                  </label>
                  <input
                    id="signup-lastname"
                    className={styles.input}
                    value={details.lastname}
                    onChange={setDetail('lastname')}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              {/* Required, and asked here rather than behind the disclosure
                  below: the storefront sells to adults, so the buyer's age is
                  part of the sale and not a profile detail to fill in later. */}
              <label className={styles.label} htmlFor="signup-dob">
                Date of birth
              </label>
              <input
                id="signup-dob"
                type="date"
                max={maxDob}
                required
                aria-required="true"
                className={styles.input}
                value={details.dob}
                onChange={setDetail('dob')}
                autoComplete="bday"
              />

              <label className={styles.label} htmlFor="signup-email">
                Email
              </label>
              <input
                id="signup-email"
                type="email"
                className={styles.input}
                value={details.email}
                onChange={setDetail('email')}
                autoComplete="email"
                spellCheck="false"
              />
              {/* Optional to the provider, but it is the only route back into an
                  account whose password has been forgotten — and that reset is
                  run from playTUB, so an account made here without one can only
                  be recovered by support. Said now rather than discovered then. */}
              <p className={styles.hint}>
                Optional — but without it, a forgotten password can&apos;t be reset.
              </p>

              <button
                type="button"
                className={styles.disclosure}
                aria-expanded={showMore}
                onClick={() => setShowMore((v) => !v)}
              >
                {showMore ? 'Hide' : 'Add'} personal details (optional)
              </button>

              {showMore && (
                <div className={styles.more}>
                  <div className={styles.row}>
                    <div>
                      <label className={styles.label} htmlFor="signup-phone">
                        Phone
                      </label>
                      <input
                        id="signup-phone"
                        className={styles.input}
                        value={details.phone}
                        onChange={setDetail('phone')}
                        autoComplete="tel"
                      />
                    </div>
                    <div>
                      <label className={styles.label} htmlFor="signup-country">
                        Country
                      </label>
                      <input
                        id="signup-country"
                        className={styles.input}
                        value={details.country}
                        onChange={setDetail('country')}
                        autoComplete="country-name"
                      />
                    </div>
                  </div>

                  <label className={styles.label} htmlFor="signup-address">
                    Address
                  </label>
                  <input
                    id="signup-address"
                    className={styles.input}
                    value={details.address}
                    onChange={setDetail('address')}
                    autoComplete="street-address"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {notice && (
          <div className={styles.notice} role="status">
            {notice}
          </div>
        )}

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={styles.submit} disabled={busy}>
            {busy
              ? isRegister
                ? 'Creating account…'
                : 'Signing in…'
              : isRegister
                ? registerLabel
                : submitLabel}
          </button>
        </div>
      </form>
    </>,
    document.body,
  );
}
